import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';

const PageContainer = styled.div`
  padding: zrem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h1`
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 2rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: 1.5rem;
`;

const Tab = styled.button`
  padding: 0.75rem 1.5rem;
  background: none;
  border: none;
  border-bottom: 2px solid ${({ $active, theme }) => $active ? theme.colors.primary : 'transparent'};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.textSecondary};
  font-weight: ${({ $active }) => $active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const PartnerCard = styled(Card)`
  height: 100%;
`;

const PartnerInfo = styled.div`
  margin-bottom: 1rem;
`;

const InfoItem = styled.div`
  display: flex;
  margin-bottom: 0.5rem;
  
  strong {
    min-width: 130px;
    font-weight: 600;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 99px;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${({ $status, theme }) => 
    $status === 'approved' ? theme.colors.success + '20' :
    $status === 'pending' ? theme.colors.warning + '20' :
    theme.colors.danger + '20'};
  color: ${({ $status, theme }) => 
    $status === 'approved' ? theme.colors.success :
    $status === 'pending' ? theme.colors.warning :
    theme.colors.danger};
  display: inline-block;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const PartnerManagement = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Fetch partners based on active tab
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        setLoading(true);
        
        // Create query based on active tab
        let partnerQuery;
        if (activeTab === 'pending') {
          partnerQuery = query(collection(db, 'partners'), where('pendingApproval', '==', true));
        } else if (activeTab === 'approved') {
          partnerQuery = query(collection(db, 'partners'), where('pendingApproval', '==', false));
        } else {
          partnerQuery = collection(db, 'partners');
        }
        
        // Fetch partners
        const querySnapshot = await getDocs(partnerQuery);
        const partnersList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setPartners(partnersList);
      } catch (err) {
        console.error('Error fetching partners:', err);
        setError('Failed to fetch partners');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPartners();
  }, [activeTab]);
  
  // Handle partner approval
  const handleApprovePartner = async (partnerId) => {
    try {
      await updateDoc(doc(db, 'partners', partnerId), {
        pendingApproval: false,
        approvedAt: new Date()
      });
      
      // Update local state
      setPartners(partners.map(partner => 
        partner.id === partnerId 
          ? { ...partner, pendingApproval: false, approvedAt: new Date() } 
          : partner
      ));
    } catch (err) {
      console.error('Error approving partner:', err);
      setError('Failed to approve partner');
    }
  };
  
  // Handle partner rejection
  const handleRejectPartner = async (partnerId) => {
    try {
      await updateDoc(doc(db, 'partners', partnerId), {
        pendingApproval: false,
        rejected: true,
        rejectedAt: new Date()
      });
      
      // Update local state
      setPartners(partners.map(partner => 
        partner.id === partnerId 
          ? { ...partner, pendingApproval: false, rejected: true, rejectedAt: new Date() } 
          : partner
      ));
    } catch (err) {
      console.error('Error rejecting partner:', err);
      setError('Failed to reject partner');
    }
  };
  
  return (
    <PageContainer>
      <Title>Partner Management</Title>
      <Subtitle>Review and manage healthcare partner accounts and applications.</Subtitle>
      
      <TabContainer>
        <Tab 
          $active={activeTab === 'pending'} 
          onClick={() => setActiveTab('pending')}
        >
          Pending Approval
        </Tab>
        <Tab 
          $active={activeTab === 'approved'} 
          onClick={() => setActiveTab('approved')}
        >
          Approved Partners
        </Tab>
        <Tab 
          $active={activeTab === 'all'} 
          onClick={() => setActiveTab('all')}
        >
          All Partners
        </Tab>
      </TabContainer>
      
      {loading && <p>Loading partners...</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}
      
      {!loading && !error && partners.length === 0 && (
        <EmptyState>
          <h3>No partners found</h3>
          <p>{activeTab === 'pending' 
            ? 'There are no pending partner requests at this time.'
            : activeTab === 'approved' 
              ? 'There are no approved partners yet.'
              : 'There are no partners in the system.'}</p>
        </EmptyState>
      )}
      
      {!loading && !error && partners.length > 0 && (
        <Grid>
          {partners.map(partner => (
            <PartnerCard key={partner.id}>
              <Card.Header>
                {partner.displayName || 'Partner'} 
                <StatusBadge 
                  $status={
                    partner.rejected ? 'rejected' : 
                    partner.pendingApproval ? 'pending' : 'approved'
                  }
                >
                  {partner.rejected ? 'Rejected' : 
                   partner.pendingApproval ? 'Pending' : 'Approved'}
                </StatusBadge>
              </Card.Header>
              <Card.Content>
                <PartnerInfo>
                  <InfoItem>
                    <strong>Email:</strong> {partner.email}
                  </InfoItem>
                  {partner.specialization && (
                    <InfoItem>
                      <strong>Specialization:</strong> {partner.specialization}
                    </InfoItem>
                  )}
                  {partner.yearsExperience && (
                    <InfoItem>
                      <strong>Experience:</strong> {partner.yearsExperience} years
                    </InfoItem>
                  )}
                  <InfoItem>
                    <strong>Registered on:</strong> {partner.createdAt ? new Date(partner.createdAt.toDate()).toLocaleDateString() : 'N/A'}
                  </InfoItem>
                </PartnerInfo>
                
                {partner.pendingApproval && (
                  <ButtonGroup>
                    <Button 
                      variant="success" 
                      onClick={() => handleApprovePartner(partner.id)}
                      fullWidth
                    >
                      Approve
                    </Button>
                    <Button 
                      variant="danger" 
                      onClick={() => handleRejectPartner(partner.id)}
                      fullWidth
                    >
                      Reject
                    </Button>
                  </ButtonGroup>
                )}
                
                {!partner.pendingApproval && !partner.rejected && (
                  <Button 
                    as={Link}
                    to={`/admin/partners/${partner.id}`} 
                    variant="outline"
                    fullWidth
                  >
                    View Details
                  </Button>
                )}
              </Card.Content>
            </PartnerCard>
          ))}
        </Grid>
      )}
    </PageContainer>
  );
};

export default PartnerManagement; 